import * as qs from "qs"
import { noCase, snakeCase, camelCase } from "change-case"

export type RecipientType = "passengers" | "text_account_holders" | "account_holders" | "emergency_contacts"
export interface BasicFilterState {
  recipientType?: RecipientType
  confirmedAndCurrentOnPayments?: true
  confirmedAndBehindOnPayments?: true
  confirmedAndComped?: true
  pendingAndMissingInfo?: true
  pendingAndNoDeposit?: true
  pendingAndPartialDeposit?: true
  onWaitlist?: true
  cancelled?: true
  includeNonTravelingTripLeaders?: true
}

export interface AdvancedFilterState {
  advanced?: boolean
  recipientType?: RecipientType
  tripLeader?: true
  questionnaireId?: number
  missingAnswer?: number | "any"
  unsignedDocument?: number | "any"
  missingInfo?: "missing_billing_info" | "any"
  onPackage?: number
  onPackageType?: number
  withAddon?: number | "any"
  withoutAddon?: number
  onListSection?: number
  insurancePlan?: "standard" | "enhanced" | "declined" | "any" | null
  autopayEnabled?: true | false | null
  paymentStatus?: "partially_paid" | "fully_paid" | "overpaid" | "cancelled_with_overages"
  paidAtLeast?: number
  paidLessThan?: number
  ageAtLeast?: number
  ageLessThan?: number
  cancelledWithNonRefundables?: number | "none"
  compedStatus?: "all" | "current" | "behind" | "pending"
}

export interface CustomListFilterState {
  recipientType?: RecipientType
  passengerPartyIdIn?: number[]
  custom?: boolean
  idIn?: number[]
}

export interface ColumnFilterState {
  accountEmail?: true
  name?: true
  nickname?: true
  phone?: true
  dateOfBirth?: true
  age?: true
  gender?: true
  added?: true
  accountHolders?: true
  address?: true
  packageName?: true
  addons?: true
  tripProtectionPlan?: true
  bookingNumber?: true
  medicalInfo?: true
  givenAnswers?: true
  listAssignments?: true
  listNotes?: true
  documents?: true
  emergencyContacts?: true
  status?: true
  netPaid?: true
  overdue?: true
  totalDue?: true
  isAutopayEnabled?: true
  isCancelled?: true
  cancellationRequestDate?: true
  cancellationProcessedDate?: true
  enrollmentId?: true
}

const columnFilters = [
  "accountEmail",
  "name",
  "nickname",
  "phone",
  "dateOfBirth",
  "age",
  "gender",
  "address",
  "added",
  "accountHolders",
  "packageName",
  "addons",
  "tripProtectionPlan",
  "bookingNumber",
  "medicalInfo",
  "givenAnswers",
  "listAssignments",
  "listNotes",
  "documents",
  "emergencyContacts",
  "partyNotes",
  "status",
  "isCancelled",
  "cancellationRequestDate",
  "cancellationProcessedDate",
  "enrollmentId",
  "onWaitlistSince",
  "confirmedAt",
  "netPaid",
  "remainingBalance",
  "totalDue",
  "overdue",
  "isAutopayEnabled",
  "packageCost",
  "addonsCost",
  "insuranceCost",
]

export type FilterState = BasicFilterState | AdvancedFilterState | CustomListFilterState | ColumnFilterState

export function defaultBasicFilters(): BasicFilterState {
  return {
    confirmedAndCurrentOnPayments: true,
    confirmedAndBehindOnPayments: true,
    confirmedAndComped: true,
    pendingAndMissingInfo: true,
    pendingAndNoDeposit: true,
    pendingAndPartialDeposit: true,
  }
}

export function toggleBooleanFilter(state: BasicFilterState, property: keyof BasicFilterState): BasicFilterState {
  let { ...newState } = state

  if (property === "recipientType") return

  if (state[property]) {
    newState[property] = undefined
  } else {
    newState[property] = true
  }

  return newState
}

export function setAdvancedFilter(state, filter) {
  state = Object.keys(state)
    .filter((key) => !key.includes("tripLeader"))
    .filter((key) => !key.includes("missingInfo"))
    .filter((key) => !key.includes("unsignedDocument"))
    .filter((key) => !key.includes("missingAnswer"))
    .filter((key) => !key.includes("withAddon"))
    .filter((key) => !key.includes("withoutAddon"))
    .filter((key) => !key.includes("onPackage"))
    .filter((key) => !key.includes("insurancePlan"))
    .filter((key) => !key.includes("autopayEnabled"))
    .filter((key) => !key.includes("paymentStatus"))
    .filter((key) => !key.includes("paidLessThan"))
    .filter((key) => !key.includes("paidAtLeast"))
    .filter((key) => !key.includes("ageAtLeast"))
    .filter((key) => !key.includes("ageLessThan"))
    .filter((key) => !key.includes("onListSection"))
    .filter((key) => !key.includes("cancelledWithNonRefundables"))
    .reduce((cur, key) => {
      return Object.assign(cur, { [key]: state[key] })
    }, {})

  state = Object.assign(state, filter)
  return state
}

export function toggleColumnFilter(state: ColumnFilterState, property: keyof ColumnFilterState): ColumnFilterState {
  let { ...newState } = state

  if (state[property]) {
    newState[property] = undefined
  } else {
    newState[property] = true
  }

  return newState
}

export function allFiltersSelected(filters, fields): boolean {
  for (let field of fields) {
    if (filters[field] === undefined) return false
  }
  return true
}

export function enableAllFilters(state: ColumnFilterState, properties: [keyof ColumnFilterState]): ColumnFilterState {
  let { ...newState } = state

  for (let property of columnFilters) {
    if (properties.find((prop) => prop === property)) {
      newState[property] = true
    } else {
      newState[property] = undefined
    }
  }

  return newState
}

export function allConfirmedAreTrue(state: BasicFilterState): boolean {
  return (
    state.confirmedAndCurrentOnPayments === true &&
    state.confirmedAndBehindOnPayments === true &&
    state.confirmedAndComped === true
  )
}

export function toggleConfirmedFilters(state: BasicFilterState): BasicFilterState {
  let { ...newState } = state

  if (allConfirmedAreTrue(state)) {
    newState.confirmedAndCurrentOnPayments = undefined
    newState.confirmedAndBehindOnPayments = undefined
    newState.confirmedAndComped = undefined
  } else {
    newState.confirmedAndCurrentOnPayments = true
    newState.confirmedAndBehindOnPayments = true
    newState.confirmedAndComped = true
  }

  return newState
}

export function allPendingAreTrue(state: BasicFilterState): boolean {
  return state.pendingAndMissingInfo && state.pendingAndNoDeposit && state.pendingAndPartialDeposit
}

export function togglePendingFilters(state: BasicFilterState): BasicFilterState {
  let { ...newState } = state

  if (allPendingAreTrue(state)) {
    newState.pendingAndMissingInfo = undefined
    newState.pendingAndNoDeposit = undefined
    newState.pendingAndPartialDeposit = undefined
  } else {
    newState.pendingAndMissingInfo = true
    newState.pendingAndNoDeposit = true
    newState.pendingAndPartialDeposit = true
  }

  return newState
}

export function toQuery(state: FilterState): string {
  let query = {}

  for (let key of Object.keys(state).sort()) {
    let snakeCased = snakeCase(noCase(key))
    let value = state[key]

    if (value === true) {
      query[snakeCased] = "true"
    } else if (value === false) {
      query[snakeCased] = "false"
    } else if (value === null) {
      query[snakeCased] = "nil"
    } else if (value !== undefined) {
      query[snakeCased] = value.toString()
    }
  }

  return qs.stringify(query, { arrayFormat: "comma", encode: false })
}

export function parseQuery(query: string): any {
  let parsed = qs.parse(query, { ignoreQueryPrefix: true, comma: true })
  let result = {}

  for (let key in parsed) {
    let camelCased = camelCase(noCase(key))
    let value = parsed[key]

    if (camelCased === "passengerPartyIdIn") {
      if (Array.isArray(value)) {
        result[camelCased] = value
      } else if (value === "") {
        result[camelCased] = []
      } else {
        result[camelCased] = [value]
      }
    } else if (value === "true") {
      result[camelCased] = true
    } else if (value === "false") {
      result[camelCased] = false
    } else if (value === "nil") {
      result[camelCased] = null
    } else if (value !== undefined) {
      result[camelCased] = value
    }
  }

  return result
}
